import { AxiosResponse } from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { default as SignaturePad } from "react-signature-canvas";
import AssignedFacilityRow from "./SignatureFacilityRow";
import {
  SaveAndAssignSignaturePayload,
  SignatureAndFacilityDetails,
} from "./types";
import useLang from "Shared/hooks/useLanguage";
import FacilityService from "Services/FacilityService/FacilityService";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import useIsMobile from "Shared/hooks/useIsMobile";

interface SignatureProps {
  id: string;
  loadGridData: (loader: boolean, reset: boolean, sortingState?: any) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const SignaturePanel: React.FC<SignatureProps> = ({
  id,
  loadGridData,
  open,
  setOpen,
}) => {
  const [signatureInfo, setSignatureInfo] =
    useState<SignatureAndFacilityDetails | null>(null);
  const [loading] = useState(false);
  const [signCanvas, setSignCanvas] = useState<SignaturePad | null>(null);
  const [assignmentsByFacilityId, setAssignmentsByFacilityId] = useState<
    Record<string, string[]>
  >({});
  const [error, setError] = useState("");
  const { t } = useLang();
  const isMobile = useIsMobile();

  const handleFacilityUserSelectionChange = useCallback(
    (facilityId: string, userIds: string[]) => {
      if (!facilityId) return;
      setAssignmentsByFacilityId((prev) => ({
        ...prev,
        [facilityId]: userIds,
      }));
    },
    []
  );

  const getAssignFacilitybyId = (userId: string) => {
    FacilityService.getSignatureAndFacilityDetailsById(userId).then(
      (res: AxiosResponse) => {
        if (res?.data?.statusCode === 200) {
          setSignatureInfo(res.data.data);
        }
      }
    );
  };

  const setCanvasRef = useCallback((node: SignaturePad | null) => {
    setSignCanvas(node);
  }, []);

  useEffect(() => {
    if (signCanvas && signatureInfo?.signature) {
      let signatureData = signatureInfo.signature;
      if (!signatureData.startsWith("data:image")) {
        signatureData = `data:image/png;base64,${signatureData}`;
      }
      signCanvas.fromDataURL(signatureData);
    }
  }, [signatureInfo?.signature, signCanvas]);

  const handleEndPhysicianSignature = async () => {
    if (!signCanvas || signCanvas.isEmpty()) {
      return;
    }
    const imageBase64 = signCanvas.toDataURL();
    setSignatureInfo((prevInfo) =>
      prevInfo ? { ...prevInfo, signature: imageBase64 } : prevInfo
    );
  };

  const clearSignature = () => {
    signCanvas?.clear();
    setSignatureInfo((prevInfo) =>
      prevInfo ? { ...prevInfo, signature: "" } : prevInfo
    );
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileInput = event.target;
    const file = fileInput.files?.[0];
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (file && allowedTypes.includes(file.type)) {
      setError("");
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Image = reader.result as string;
        if (signCanvas) {
          signCanvas.fromDataURL(base64Image);
        }
        setSignatureInfo((prevInfo) =>
          prevInfo ? { ...prevInfo, signature: base64Image } : prevInfo
        );
      };
      reader.readAsDataURL(file);
    } else {
      setError(
        t("Invalid file type or size. Please upload a valid image file.")
      );
    }
    fileInput.value = "";
  };

  useEffect(() => {
    setAssignmentsByFacilityId({});
  }, [id]);

  useEffect(() => {
    getAssignFacilitybyId(id);
  }, [id]);

  const buildAssignedUserIds =
    (): SaveAndAssignSignaturePayload["assignedUserIds"] => {
      const pairs: SaveAndAssignSignaturePayload["assignedUserIds"] = [];
      for (const [facilityId, userIds] of Object.entries(
        assignmentsByFacilityId
      )) {
        for (const userId of userIds) {
          pairs.push({
            userId: String(userId),
            facilityId: String(facilityId),
          });
        }
      }
      return pairs;
    };

  const handleSave = async () => {
    if (!signatureInfo?.signature) {
      toast.error(t("Please provide your signature before saving."));
      return;
    }

    const userId = String(signatureInfo.userId ?? id);
    const payload: SaveAndAssignSignaturePayload = {
      userSignature: signatureInfo.signature,
      userId,
      assignedUserIds: buildAssignedUserIds(),
    };

    try {
      const res = await FacilityService.saveAndAssignSignature(payload);
      if (res?.status === 200) {
        toast.success(t(res?.data?.responseMessage ?? res?.data?.message));
        loadGridData(true, false);
      }
    } catch (err: unknown) {
      console.trace(err);
      toast.error(t("An error occurred while saving."));
    }
  };

  return (
    <>
      <div className=" d-flex justify-content-between align-items-center my-3 px-2">
        <div className="d-flex align-items-center justify-content-between gap-3">
          <PermissionComponent
            moduleName="Facility"
            pageName="Facility Options"
            permissionIdentifier="Save"
          >
            {({ pageName, moduleName, permissionIdentifier }) => (
              <button
                id="FacilityOptionSave"
                className="btn btn-primary btn-sm fw-500"
                onClick={handleSave}
                type="button"
              >
                {getPermissionDisplayName(
                  pageName,
                  moduleName,
                  permissionIdentifier,
                  t("Save")
                )}
              </button>
            )}
          </PermissionComponent>
          <button
            id="FacilityOptionCancel"
            className="btn btn-secondary btn-sm btn-secondary--icon"
            onClick={() => setOpen(!open)}
            type="button"
          >
            {t("Cancel")}
          </button>
        </div>
        <div className="m-0 fs-4 lead fw-500"></div>
      </div>
      <div className="py-0">
        <div className="card border border-1 border-gray-300 mb-3 rounded">
          <div className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4">
            <h6 className="mb-0">{t("Upload Signature")}</h6>
          </div>
          <div className="card-body py-md-4 py-3 px-4">
            <div className="d-flex flex-wrap gap-3">
              <div className="gap-2 mt-2">
                <div>
                  <input
                    type="file"
                    id="upload-file"
                    className="d-none"
                    onChange={handleFileChange}
                    accept="image/png,image/jpeg,image/jpg"
                  />
                  <label
                    htmlFor="upload-file"
                    className="dropzone pt-2 py-1 px-8 d-flex align-items-center"
                  >
                    <div className="dz-message needsclick w-100 d-flex justify-content-center">
                      {t("Choose File")}
                    </div>
                  </label>
                </div>
                <div>
                  <span className="text-muted">
                    {t("Note! Please choose only PNG | .JPG | .JPEG")}
                  </span>
                  {error && (
                    <div className="text-dark-65 form__error mt-2">
                      <span>{error}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="w-100">
                <SignaturePad
                  maxWidth={2}
                  penColor="black"
                  ref={setCanvasRef}
                  backgroundColor="white"
                  onEnd={handleEndPhysicianSignature}
                  canvasProps={{
                    style: {
                      border: "2px dotted #c9d2dd",
                      borderRadius: "4px",
                      width: "100%",
                    },
                  }}
                />
                <div className="d-flex align-items-center justify-content-between">
                  <label>{t("Physician's Signature")}</label>
                  <button
                    className="btn btn-secondary btn-sm fw-bold"
                    type="button"
                    onClick={clearSignature}
                  >
                    {t("Remove Sign")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <h4 className="text-primary text-start my-2">
          {t("Assigned Facilities Users")}
        </h4>
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              sx={
                isMobile
                  ? {}
                  : {
                      maxHeight: 800,
                      "&::-webkit-scrollbar": {
                        width: 7,
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#fff",
                      },
                      "&:hover": {
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "var(--kt-gray-400)",
                          borderRadius: 2,
                        },
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--kt-gray-400)",
                        borderRadius: 2,
                      },
                    }
              }
              className="shadow-none"
            >
              <Table
                aria-label="sticky table collapsible"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  <TableRow className="h-30px">
                    <TableCell
                      className="min-w-150px"
                      sx={{ width: "max-content" }}
                    >
                      <div style={{ width: "max-content" }}>
                        {t("Facility Name")}
                      </div>
                    </TableCell>
                    <TableCell
                      className="min-w-150px"
                      sx={{ width: "max-content" }}
                    >
                      <div style={{ width: "max-content" }}>
                        {t("Select All User")}
                      </div>
                    </TableCell>
                    <TableCell
                      className="min-w-150px"
                      sx={{ width: "max-content" }}
                    >
                      <div style={{ width: "max-content" }}>
                        {t("User Name")}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="padding-0">
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : signatureInfo?.userAssignedFacilities?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="padding-0">
                        <NoRecord message={"No data found for this table"} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    signatureInfo?.userAssignedFacilities?.map(
                      (item, index: number) => (
                        <AssignedFacilityRow
                          row={item}
                          key={`${item.facilityId ?? item.FacilityId ?? index}`}
                          onFacilityUserSelectionChange={
                            handleFacilityUserSelectionChange
                          }
                        />
                      )
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>
    </>
  );
};

export default SignaturePanel;
